"use client";
import React, { useState, useRef, useEffect } from "react";
import { BriefcaseIcon, TagIcon } from "@heroicons/react/24/outline";
import { usePathname } from "next/navigation";

const options = [
  { type: 'title', value: 'Engineer' },
  { type: 'title', value: 'Frontend' },
  { type: 'title', value: 'Backend' },
  { type: 'title', value: 'Data' },
  { type: 'title', value: 'DevOps' },
  { type: 'title', value: 'Marketing' },
  { type: 'title', value: 'Sales' },
  { type: 'title', value: 'Copywriter' },
  { type: 'title', value: 'Technical writer' },
  { type: 'title', value: 'Design' },
  { type: 'title', value: 'Fullstack' },
  { type: 'title', value: 'Quality Assurance' },
  { type: 'title', value: 'Security Engineer' },
  { type: 'title', value: 'Project Manager' },
  { type: 'title', value: 'Product Manager' },
  { type: 'title', value: 'Finance' },
  { type: 'title', value: 'Legal' },
  { type: 'title', value: 'Executive Assistant' },
  { type: 'title', value: 'Customer Support' },
  { type: 'title', value: 'Customer Service' },
  { type: 'title', value: 'Customer Success' },
  { type: 'title', value: 'HR' },
  { type: 'title', value: 'Mobile' },
  { type: 'title', value: 'Finance' },
  { type: 'title', value: 'Legal' },
  { type: 'title', value: 'Analyst' },
  { type: 'title', value: 'Accounting' },
  { type: 'title', value: 'Admin' },
  { type: 'tag', value: 'sql' },
  { type: 'tag', value: 'postgresql' },
  { type: 'tag', value: 'pulumi' },
  { type: 'tag', value: 'javascript' },
  { type: 'tag', value: 'typescript' },
  { type: 'tag', value: 'elixir' },
  { type: 'tag', value: 'finance' },
  { type: 'tag', value: 'management' },
  { type: 'tag', value: 'engineering' },
  { type: 'tag', value: 'b2c' },
  { type: 'tag', value: 'b2b' },
  { type: 'tag', value: 'legal' },
  { type: 'tag', value: 'cyber security' },
  { type: 'tag', value: 'database' },
  { type: 'tag', value: 'mysql' },
  { type: 'tag', value: 'mongo' },
  { type: 'tag', value: 'python' },
  { type: 'tag', value: 'java' },
  { type: 'tag', value: 'aws' },
  { type: 'tag', value: 'azure' },
  { type: 'tag', value: 'gcp' },
  { type: 'tag', value: 'kubernetes' },
  { type: 'tag', value: 'ios' },
  { type: 'tag', value: 'android' },
  { type: 'tag', value: 'product' },
]

const SearchableDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const pathname = usePathname();

  useEffect(() => {
    if (pathname.startsWith('/remote-')) {
      let activeOption = decodeURIComponent(pathname).replace('/remote-', '').replace('-jobs', '').replaceAll(/\/page\/[0-9]+/g, '').replaceAll(/-/g, ' ');
      setSearchTerm(activeOption);
    }
  }, [pathname]);

  const filteredOptions = options.filter((option) =>
    option.value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Improved click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside both the dropdown and input
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    // Handle both mouse and touch events
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchend", handleClickOutside);
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchend", handleClickOutside);
    };
  }, []);

  const handleInputFocus = () => {
    // Small delay to ensure proper focus handling on mobile
    setTimeout(() => {
      setIsOpen(true);
    }, 100);
  };

  const handleOptionClick = (optionValue) => {
    setIsOpen(false);
    // Blur the input to hide the keyboard on mobile
    inputRef.current?.blur();
  };

  return (
    <div className="relative w-72 dropdown" ref={dropdownRef}>
      <label className="input input-bordered">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 16 16"
          fill="currentColor"
          className="h-4 w-4 opacity-70 shrink-0"
        >
          <path
            fillRule="evenodd"
            d="M9.965 11.026a5 5 0 1 1 1.06-1.06l2.755 2.754a.75.75 0 1 1-1.06 1.06l-2.755-2.754ZM10.5 7a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z"
            clipRule="evenodd"
          />
        </svg>
        <input
          ref={inputRef}
          type="search"
          className="grow outline-hidden w-full capitalize"
          placeholder="Search by title or skill..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={handleInputFocus}
          role="combobox"
          aria-expanded={isOpen}
          aria-controls="search-listbox"
          aria-autocomplete="list"
        />
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          viewBox="0 0 26 26"
          className="h-4 w-4 opacity-70 shrink-0"
        >
          <path d="M 6.414063 7.585938 L 3.585938 10.414063 L 13 19.828125 L 22.414063 10.414063 L 19.585938 7.585938 L 13 14.171875 Z"></path>
        </svg>
      </label>
      
      {isOpen && (
        <ul 
          id="search-listbox"
          role="listbox"
          className="dropdown-content menu w-72 sm:max-w-sm p-2 bg-[#222] flex-nowrap absolute left-0 right-0 mt-2 max-h-80 overflow-y-auto rounded-lg shadow-[inset_0_-20px_20px_-20px_rgba(0,0,0,0.3)] z-50 ring-1 ring-inset ring-gray-500/20 "
          style={{
            WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
          }}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <li 
                key={index}
                role="option"
                aria-selected={false}
                className="rounded-md cursor-pointer"
                onClick={() => handleOptionClick(option.value)}
              >
                <a
                  href={`/remote-${option.value
                    .toLowerCase()
                    .replace(/ /g, "-")}-jobs`}
                  className="flex items-center gap-2 w-full py-2"
                >
                  {option.type === "title" ? (
                    <BriefcaseIcon className="h-4 w-4 shrink-0" />
                  ) : (
                    <TagIcon className="h-4 w-4 shrink-0" />
                  )}
                  <span className="truncate">{option.value}</span>
                </a>
              </li>
            ))
          ) : (
            <li className="px-4 py-2 text-center text-gray-500">
              No results found
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default SearchableDropdown;